import { safetyNumber, sha256ConversationId, fromHex, toHex } from "@veto/crypto-bridge";
import type { Account, Contact, Conversation } from "@veto/domain";
import {
  addSecurityEvent,
  getContact,
  listConversations,
  saveContact,
  upsertConversation,
} from "@veto/storage";
import type { SessionBundle } from "./accountLifecycle";
import { applyDefaultDisappear } from "../chatPrefs";

async function directConversationFor(
  session: SessionBundle,
  remoteMsg: Uint8Array,
): Promise<string> {
  return toHex(await sha256ConversationId(session.identity.messagingPublicKey(), remoteMsg));
}

async function ensureConversation(
  convId: string,
  contact: Contact,
  preview: string,
): Promise<void> {
  const existing = (await listConversations()).find((c) => c.id === convId);
  const row: Conversation = existing
    ? { ...existing, title: contact.displayName, contactId: contact.id, requestPending: undefined, archived: false }
    : {
        id: convId,
        type: "direct",
        title: contact.displayName,
        contactId: contact.id,
        lastMessagePreview: preview,
        lastMessageAt: Date.now(),
        unread: 0,
        pinned: false,
        muted: false,
      };
  await upsertConversation(row);
  // A brand-new chat starts with "Disappearing messages for new chats" (phone parity).
  if (!existing) await applyDefaultDisappear(convId);
}

/**
 * Add (or re-add) a contact from verified invite keys (FR-CON-001).
 * Re-adding someone we already have never resets their verification, block
 * state, profile or chat settings; adding a pending message request accepts it.
 */
export async function addContactFlow(opts: {
  session: SessionBundle;
  account: Account;
  displayName: string;
  hybridPubHex: string;
  messagingPubHex: string;
  /** Peer BIP-340 x-only transport key — from the invite bundle. */
  transportPubHex?: string;
  onKeyChange: () => void;
}): Promise<{ conversationId: string; verificationState: Contact["verificationState"]; created: boolean }> {
  const { session } = opts;
  const displayName = opts.displayName.trim().slice(0, 64);
  if (!displayName) throw new Error("Display name required");
  const messagingPubHex = opts.messagingPubHex.toLowerCase();
  const hybridPubHex = opts.hybridPubHex.toLowerCase();
  if (messagingPubHex === opts.account.messagingPublicKeyHex.toLowerCase()) {
    throw new Error("That's your own invite — share it with someone else");
  }
  const remoteMsg = fromHex(messagingPubHex);
  const sn = safetyNumber(
    session.identity.messagingPublicKey(),
    remoteMsg,
    session.identity.hybridPublicKey(),
    fromHex(hybridPubHex),
  );
  const id = messagingPubHex.slice(0, 16);
  const convId = await directConversationFor(session, remoteMsg);
  const existing = await getContact(id);

  if (
    existing &&
    existing.messagingPublicKeyHex.toLowerCase() === messagingPubHex &&
    existing.hybridPublicKeyHex.toLowerCase() === hybridPubHex
  ) {
    const next: Contact = {
      ...existing,
      displayName,
      transportPublicKeyHex: existing.transportPublicKeyHex ?? opts.transportPubHex,
      requestState: undefined,
      addedVia: existing.addedVia ?? "invite",
    };
    await saveContact(next);
    await ensureConversation(convId, next, "Say hi 👋");
    return { conversationId: convId, verificationState: next.verificationState, created: false };
  }

  let verificationState: Contact["verificationState"] = "unverified";
  if (existing) {
    verificationState = "changed";
    await addSecurityEvent({
      kind: "key_change",
      contactId: id,
      detail: `Keys changed for ${displayName}. Previous safety number began ${existing.safetyNumber.slice(0, 20)}… — verify in person before trusting.`,
    });
    opts.onKeyChange();
  }
  const contact: Contact = {
    ...(existing ?? {}),
    id,
    displayName,
    messagingPublicKeyHex: messagingPubHex,
    hybridPublicKeyHex: hybridPubHex,
    transportPublicKeyHex: opts.transportPubHex,
    safetyNumber: sn,
    verificationState,
    requestState: undefined,
    addedVia: "invite",
    addedAt: existing?.addedAt ?? Date.now(),
  };
  await saveContact(contact);
  await ensureConversation(
    convId,
    contact,
    verificationState === "changed" ? "⚠ Security code changed — verify" : "Say hi 👋",
  );
  return { conversationId: convId, verificationState, created: !existing };
}

/** Accept a message request: the sender becomes a regular (unverified) contact. */
export async function acceptMessageRequest(contactId: string): Promise<void> {
  const contact = await getContact(contactId);
  if (!contact) return;
  await saveContact({ ...contact, requestState: undefined });
  for (const conv of await listConversations()) {
    if (conv.contactId === contactId && conv.requestPending) {
      await upsertConversation({ ...conv, requestPending: undefined });
      // Accepting turns the request into a chat: it starts with the default timer.
      await applyDefaultDisappear(conv.id);
    }
  }
}
